<template>
  <div class="tax-return-list">

    <div class="tax-return-list-wrap">
      <TitleBar>
        <template v-slot:title>
          <translate>Tax return for</translate>
          {{ selectedYear }}
        </template>

        <template v-slot:buttons>
          <div class="buttons">
            <b-form-select
              class="select"
              v-model="selectedYear"
              :disabled="isSubmitting || isLoading"
              @change="fetchData"
            >
              <b-form-select-option v-for="year in years" :value="year" :key="year">
                {{ year }}
              </b-form-select-option>
            </b-form-select>

            <GekkoButton
              :text="$gettext('Calculate')"
              :disabled="isSubmitting || isLoading"
              icon="fa-solid fa-refresh"
              @click.prevent="calculate"
            />

            <GekkoButton
              v-bind:text="$gettext('Save')"
              :disabled="isSubmitting || isLoading"
              icon="fa-solid fa-save"
              @click.prevent="save"
            />

            <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown" variant="outline-primary" right no-caret>
              <template #button-content>
                <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
              </template>

              <b-dropdown-item @click="download()">
                <font-awesome-icon icon="fa-solid fa-download" />
                <translate>Download</translate>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </TitleBar>
      <div v-if="hasFailed">Oops. We ran into an issue.</div>
      <div class="content">
        <LoadingSpinner v-if="isLoading" />

        <TaxReturnSections v-if="!isLoading && !hasFailed && data" :data="data" />

        <EmptyState v-else>
          <template v-slot:image>
            <div class="image-text-container">
              <div class="image-container">
                <img src="/static/img/empty-states/empty-state-cost.png" alt="Empty state" />
              </div>
            </div>
          </template>

          <template v-slot:title>
            <translate>No data to show for this year.</translate>
          </template>

          <template v-slot:content> </template>

          <template v-slot:cta> </template>
        </EmptyState>
      </div>
    </div>
  </div>
</template>

<script>
import settingsPageSetup from '@/mixins/settingsPageSetup'
import TitleBar from '@/components/TitleBar.vue'
import OnboardingIntroLink from '@/components/ui/OnboardingIntroLink.vue'
import ReportTable from '@/components/balance_sheet/ReportTable.vue'
import GekkoButton from '@/components/GekkoButton.vue'
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import TaxReturnRevenueSection from '@/components/accountants/tax_return/sections/TaxReturnRevenueSection.vue'
import Expandable from '@/components/Expandable.vue'
import TaxReturnTableRow from '@/components/accountants/tax_return/table/TaxReturnTableRow.vue'
import TaxReturnSections from '@/components/accountants/tax_return/TaxReturnSections.vue'
import { mapGetters } from 'vuex'
import TaxReturnService from '@/store/services/taxreturn.service'
import CustomerService from '@/store/services/customer.service'

export default {
  name: 'TaxReturn',
  mixins: [settingsPageSetup],
  components: {
    TaxReturnSections,
    TaxReturnTableRow,
    Expandable,
    TaxReturnRevenueSection,
    EmptyState,
    LoadingSpinner,
    GekkoButton,
    ReportTable,
    OnboardingIntroLink,
    TitleBar,
  },
  mounted() {
    // Band-aid fix for not being able to scroll
    document.body.classList.remove('modal-on')

    this.fetchData()
  },
  data() {
    return {
      pageSettings: {
        title: this.$gettext('Tax return'),
      },
      hasFailed: false,
      isLoading: true,
      data: null,
      selectedYear: 2023,
      isSubmitting: false,
      customerName: '',
    }
  },
  methods: {
    async fetchData() {
      await this.save()

      this.isLoading = true
      this.hasFailed = false
      this.data = null

      try {
        const response = await TaxReturnService.retrieve(this.getCompanyId, this.selectedYear)

        this.data = response.data
      } catch (error) {
        if ((error.status && error.status === 404) || (error.response && error.response.status === 404)) {
          const response = await TaxReturnService.create(this.getCompanyId, this.selectedYear)
          this.data = response.data
          return
        }

        console.error(error)

        this.hasFailed = true

        this.$store.commit('setAlert', {
          title: this.$gettext('Unable to fetch your data'),
          text: this.$gettext('We were unable to fetch your data. Please try again in a moment.'),
          variant: 'danger',
          duration: 5,
        })
      } finally {
        this.isLoading = false
      }
    },
    async save() {
      if (!this.data) return

      this.isSubmitting = true

      try {
        const response = await TaxReturnService.update(this.data.id, {
          id: this.data.id,
          company: this.data.company,
          year: this.data.year,
          edit: this.data.edit,
          energy_and_climate_investments: this.data.energy_and_climate_investments,
          small_scale_deductions: this.data.small_scale_deductions,
          right_self_employment_deduction: this.data.right_self_employment_deduction,
          self_employment_deduction_previous_years: this.data.self_employment_deduction_previous_years,
          right_starter_deduction: this.data.right_starter_deduction,
          received_starter_deduction_past_5_years: this.data.received_starter_deduction_past_5_years,
          times_starter_deduction_received_last_5_years: this.data.times_starter_deduction_received_last_5_years,
        })

        this.data = response.data

        this.$store.commit('setAlert', {
          title: this.$gettext('Changes saved'),
          text: this.$gettext(`Successfully saved your changes for year {year}`).replace('{year}', response.data.year),
          variant: 'success',
          duration: 5,
        })
      } catch (error) {
        this.$store.commit('setAlert', {
          title: this.$gettext('Changes not saved!'),
          text: this.$gettext(`Failed to save your changes! Please try again.`),
          variant: 'danger',
          duration: 5,
        })
      } finally {
        this.isSubmitting = false
      }
    },
    async calculate() {
      await this.save()

      this.isSubmitting = true

      try {
        const response = await TaxReturnService.calculate(this.data.id)

        this.data = response.data
      } catch (error) {
        this.$store.commit('setAlert', {
          title: this.$gettext('Failed to calculate values'),
          text: this.$gettext('Failed to calculate the values for your tax return. Try again in a moment.'),
          variant: 'danger',
          duration: 5,
        })
      } finally {
        this.isSubmitting = false
      }
    },
    download() {
      TaxReturnService.download(this.data.id).then((blob) => {
        const link = document.createElement('a')
        link.href = blob
        link.download = `Winstaangifte ${this.customerName} ${this.data.year}.xml`

        document.body.appendChild(link)

        link.click()

        document.body.removeChild(link)
        URL.revokeObjectURL(blob)
      })
    },
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
    }),
    years() {
      // TODO: I don't know how to generate a range of numbers in JS...
      return [2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017]
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (this.$route.params['customerId']) {
          CustomerService.retrieve(this.$route.params['customerId']).then((response) => {
            this.customerName = response.data.name
          })
        }
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.tax-return-list {
  display: flex;
  flex: 1;
  // padding: 0 16px;
}
.tax-return-list-wrap {
  // overflow: hidden;
}
.content {
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
}

tr {
  margin: 10px 20px;
}

.select {
  width: 5rem;
}

.buttons {
  display: flex;
  flex-direction: row;
  gap: 10px;
}
</style>
